<template>
    <Dialog v-model:open="isOpen" :dir="$dir.value">
        <DialogContent class="grid-rows-[auto_1fr_auto] px-0 sm:max-w-full h-svh md:h-screen">
            <DialogHeader class="sm:text-center">
                <DialogTitle>{{ $t("product.view_image.title") }}</DialogTitle>
                <DialogDescription>{{ $t("product.view_image.description") }}</DialogDescription>
            </DialogHeader>
            <div class="flex max-md:flex-col items-center w-full max-w-full h-full max-h-full overflow-hidden">
                <div
                    ref="viewport"
                    class="w-full h-full overflow-hidden touch-none select-none"
                    @wheel.prevent="onWheel"
                    @pointerdown="onPointerDown"
                    @dblclick="onDoubleClick"
                >
                    <div ref="content" class="flex justify-center items-center w-full h-full will-change-transform" style="transform-origin: 0 0">
                        <Image :desktop="image" class="w-full max-w-full h-full max-h-full object-contain pointer-events-none select-none" />
                    </div>
                </div>
                <div class="p-4">
                    <Slider
                        v-model="sliderScale"
                        :orientation="isDesktop ? 'vertical' : 'horizontal'"
                        :max="maxScale"
                        :min="minScale"
                        :step="0.01"
                        :class="isDesktop ? 'h-36' : 'w-64 mx-auto'"
                    />
                </div>
            </div>
            <DialogFooter class="sm:justify-start px-4">
                <DialogClose as-child>
                    <Button type="button" variant="destructive">{{ $t("general.close") }}</Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import useDevice from "~/composables/device";

interface Props {
    image?: APIMediaCollection | APIMedia | string;
}

const isOpen = defineModel<boolean>("open");
defineProps<Props>();

const { isDesktop } = useDevice();

const viewport = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);

const minScale = 1;
const maxScale = 10;

const state = {
    x: 0,
    y: 0,
    scale: 1,
};

const stateScale = ref(1);

const sliderScale = computed({
    get() {
        return [stateScale.value];
    },
    set(v: number[]) {
        const val = v[0] ?? 1;
        if (Math.abs(val - state.scale) > 0.01) {
            const rect = viewport.value?.getBoundingClientRect();
            if (rect) {
                const rX = rect.width / 2;
                const rY = rect.height / 2;
                const s = val / state.scale;
                state.x = rX - (rX - state.x) * s;
                state.y = rY - (rY - state.y) * s;
            }
            state.scale = val;
            updateTransform();
        }
    },
});

function updateTransform() {
    if (!content.value) return;

    if (viewport.value) {
        const rect = viewport.value.getBoundingClientRect();
        const minX = rect.width * (1 - state.scale);
        const minY = rect.height * (1 - state.scale);

        state.x = Math.max(minX, Math.min(0, state.x));
        state.y = Math.max(minY, Math.min(0, state.y));
    }

    content.value.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;

    if (Math.abs(stateScale.value - state.scale) > 0.01) {
        stateScale.value = state.scale;
    }
}

// Pointer tracking
const pointers = new Map<number, { x: number; y: number }>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let gesture: any = null;
let inertiaRaf: number | null = null;

function cancelInertia() {
    if (inertiaRaf !== null) {
        cancelAnimationFrame(inertiaRaf);
        inertiaRaf = null;
    }
}

function onWheel(e: WheelEvent) {
    if (!viewport.value) return;
    cancelInertia();

    const zoomSensitivity = e.ctrlKey ? 0.01 : 0.001;
    const delta = -e.deltaY * zoomSensitivity;

    let newScale = state.scale * Math.exp(delta);
    newScale = Math.max(minScale, Math.min(maxScale, newScale));

    const rect = viewport.value.getBoundingClientRect();
    const rX = e.clientX - rect.left;
    const rY = e.clientY - rect.top;

    const s = newScale / state.scale;
    state.x = rX - (rX - state.x) * s;
    state.y = rY - (rY - state.y) * s;
    state.scale = newScale;

    updateTransform();
}

function onPointerDown(e: PointerEvent) {
    if (!viewport.value) return;
    viewport.value.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    cancelInertia();

    if (pointers.size === 1) {
        gesture = {
            type: "pan",
            startX: state.x,
            startY: state.y,
            pointerX: e.clientX,
            pointerY: e.clientY,
            lastX: state.x,
            lastY: state.y,
            lastTime: e.timeStamp,
            vx: 0,
            vy: 0,
        };
    } else if (pointers.size === 2) {
        const pts = Array.from(pointers.values());
        const center = { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 };
        const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
        const rect = viewport.value.getBoundingClientRect();

        gesture = {
            type: "pinch",
            startScale: state.scale,
            startX: state.x,
            startY: state.y,
            startDistance: dist,
            startCenter: center,
            rect,
        };
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
}

function onPointerMove(e: PointerEvent) {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (!gesture) return;

    if (gesture.type === "pan" && pointers.size === 1) {
        const dx = e.clientX - gesture.pointerX;
        const dy = e.clientY - gesture.pointerY;

        state.x = gesture.startX + dx;
        state.y = gesture.startY + dy;

        const dt = e.timeStamp - gesture.lastTime;
        if (dt > 0) {
            const ivx = (state.x - gesture.lastX) / dt;
            const ivy = (state.y - gesture.lastY) / dt;
            gesture.vx = 0.8 * gesture.vx + 0.2 * ivx;
            gesture.vy = 0.8 * gesture.vy + 0.2 * ivy;
        }
        gesture.lastX = state.x;
        gesture.lastY = state.y;
        gesture.lastTime = e.timeStamp;

        updateTransform();
    } else if (gesture.type === "pinch" && pointers.size === 2) {
        const pts = Array.from(pointers.values());
        const center = { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 };
        const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);

        let newScale = gesture.startScale * (dist / gesture.startDistance);
        newScale = Math.max(minScale, Math.min(maxScale, newScale));

        const rStartX = gesture.startCenter.x - gesture.rect.left;
        const rStartY = gesture.startCenter.y - gesture.rect.top;

        const rCenterX = center.x - gesture.rect.left;
        const rCenterY = center.y - gesture.rect.top;

        const s = newScale / gesture.startScale;
        state.x = rCenterX - (rStartX - gesture.startX) * s;
        state.y = rCenterY - (rStartY - gesture.startY) * s;
        state.scale = newScale;

        updateTransform();
    }
}

function onPointerUp(e: PointerEvent) {
    pointers.delete(e.pointerId);

    if (pointers.size === 1) {
        const pts = Array.from(pointers.values());
        gesture = {
            type: "pan",
            startX: state.x,
            startY: state.y,
            pointerX: pts[0]!.x,
            pointerY: pts[0]!.y,
            lastX: state.x,
            lastY: state.y,
            lastTime: performance.now(),
            vx: 0,
            vy: 0,
        };
    } else if (pointers.size === 0) {
        if (gesture && gesture.type === "pan") {
            startInertia(gesture.vx, gesture.vy);
        }
        gesture = null;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
    }
}

function startInertia(vx: number, vy: number) {
    if (state.scale <= 1) return;

    let currentVx = vx * 16;
    let currentVy = vy * 16;
    const friction = 0.92;

    function step() {
        if (Math.abs(currentVx) < 0.1 && Math.abs(currentVy) < 0.1) {
            inertiaRaf = null;
            return;
        }

        state.x += currentVx;
        state.y += currentVy;

        currentVx *= friction;
        currentVy *= friction;

        updateTransform();
        inertiaRaf = requestAnimationFrame(step);
    }

    inertiaRaf = requestAnimationFrame(step);
}

function resetView() {
    cancelInertia();
    state.scale = 1;
    state.x = 0;
    state.y = 0;
    updateTransform();
}

function onDoubleClick(e: MouseEvent) {
    if (!viewport.value) return;
    const rect = viewport.value.getBoundingClientRect();
    const rX = e.clientX - rect.left;
    const rY = e.clientY - rect.top;

    const targetScale = state.scale > 1.5 ? 1 : 2.5;
    animateZoom(targetScale, rX, rY);
}

function animateZoom(targetScale: number, rX: number, rY: number) {
    cancelInertia();

    const startScale = state.scale;
    const startX = state.x;
    const startY = state.y;

    const s = targetScale / startScale;
    let targetX = rX - (rX - startX) * s;
    let targetY = rY - (rY - startY) * s;

    if (viewport.value) {
        const rect = viewport.value.getBoundingClientRect();
        const minX = rect.width * (1 - targetScale);
        const minY = rect.height * (1 - targetScale);
        targetX = Math.max(minX, Math.min(0, targetX));
        targetY = Math.max(minY, Math.min(0, targetY));
    }

    const startTime = performance.now();
    const duration = 250;

    function step(now: number) {
        const progress = Math.min(1, (now - startTime) / duration);
        const ease = 1 - Math.pow(1 - progress, 3);

        state.scale = startScale + (targetScale - startScale) * ease;
        state.x = startX + (targetX - startX) * ease;
        state.y = startY + (targetY - startY) * ease;

        updateTransform();

        if (progress < 1) {
            inertiaRaf = requestAnimationFrame(step);
        } else {
            inertiaRaf = null;
        }
    }

    inertiaRaf = requestAnimationFrame(step);
}

watch(isOpen, async (open) => {
    if (open) {
        await nextTick();
        resetView();
    }
});

onMounted(() => {
    if (isOpen.value) {
        resetView();
    }
});

onUnmounted(() => {
    cancelInertia();
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
});
</script>

<style></style>
